/**
 * Official Azerbaijan Payroll & Employer Cost Calculator (2026 Standards)
 * Reference: Tax Code of the Republic of Azerbaijan (Articles 101.3, 101.2, 102),
 * Law of the Republic of Azerbaijan on Social Insurance and Medical Insurance.
 */

export const calculateOfficialPayroll = (params, mode = 'GROSS_TO_NET') => {
  const {
    amount = 0,
    sector = 'PRIVATE',
    workplaceType = 'MAIN',
    unionFeePercent = 0,
    exemptionAmount = 0,
    year = 2026
  } = params;

  const calculate = (gross) => {
    let incomeTax = 0;
    let dsmfEmployee = 0;
    let itsEmployee = 0;
    let unemploymentEmployee = 0;
    let dsmfEmployer = 0;
    let itsEmployer = 0;
    let unemploymentEmployer = 0;

    // Relief (Güzəşt): 200 AZN basic relief only applies to Main Workplace
    const basicRelief = workplaceType === 'MAIN' ? 200 : 0;
    const effectiveExemption = workplaceType === 'MAIN' ? (Number(exemptionAmount) || 0) : 0;

    // 1. Unemployment insurance (0.5% each for employee and employer)
    unemploymentEmployee = gross * 0.005;
    unemploymentEmployer = gross * 0.005;

    // Taxable base for income tax
    const taxableIncome = Math.max(0, gross - basicRelief - effectiveExemption);

    if (sector === 'PRIVATE') {
      /** PRIVATE / NON-OIL SECTOR **/
      if (year >= 2026) {
        if (taxableIncome <= 2500) {
          incomeTax = taxableIncome * 0.03;
        } else if (taxableIncome <= 8000) {
          incomeTax = 75 + (taxableIncome - 2500) * 0.10;
        } else {
          incomeTax = 625 + (taxableIncome - 8000) * 0.14;
        }
      } else {
        if (gross <= 8000) {
          incomeTax = 0;
        } else {
          incomeTax = (gross - 8000) * 0.14;
        }
      }

      // DSMF Employee: up to 200 AZN 3%, then 6 + (Gross - 200) * 10%
      if (gross <= 200) {
        dsmfEmployee = gross * 0.03;
      } else {
        dsmfEmployee = 6 + (gross - 200) * 0.10;
      }

      // DSMF Employer: up to 200 AZN 22%, 200-8000 AZN 44 + 15%, >8000 AZN 1214 + 11%
      if (gross <= 200) {
        dsmfEmployer = gross * 0.22;
      } else if (gross <= 8000) {
        dsmfEmployer = 44 + (gross - 200) * 0.15;
      } else {
        dsmfEmployer = 1214 + (gross - 8000) * 0.11;
      }

      // ITS (Mandatory Health Insurance): 2% up to 2500, 0.5% above 2500
      if (gross <= 2500) {
        itsEmployee = gross * 0.02;
        itsEmployer = gross * 0.02;
      } else {
        itsEmployee = 50 + (gross - 2500) * 0.005;
        itsEmployer = 50 + (gross - 2500) * 0.005;
      }
    } else {
      /** STATE / OIL-GAS SECTOR **/
      if (gross <= 2500) {
        incomeTax = taxableIncome * 0.14;
      } else {
        const baseTaxAt2500 = Math.max(0, 2500 - basicRelief - effectiveExemption) * 0.14;
        incomeTax = baseTaxAt2500 + (gross - 2500) * 0.25;
      }

      // DSMF: 3% employee, 22% employer flat
      dsmfEmployee = gross * 0.03;
      dsmfEmployer = gross * 0.22;

      // ITS: 2% up to 8000, 0.5% on excess
      if (gross <= 8000) {
        itsEmployee = gross * 0.02;
        itsEmployer = gross * 0.02;
      } else {
        itsEmployee = 160 + (gross - 8000) * 0.005;
        itsEmployer = 160 + (gross - 8000) * 0.005;
      }
    }

    const unionFee = gross * ((Number(unionFeePercent) || 0) / 100);
    const totalEmployeeDeductions = incomeTax + dsmfEmployee + itsEmployee + unemploymentEmployee + unionFee;
    const net = gross - totalEmployeeDeductions;
    const totalEmployerCost = dsmfEmployer + itsEmployer + unemploymentEmployer;
    const superGross = gross + totalEmployerCost;

    return {
      gross: Number(gross.toFixed(2)),
      net: Number(net.toFixed(2)),
      taxableIncome: Number(taxableIncome.toFixed(2)),
      basicRelief,
      exemptionAmount: effectiveExemption,
      incomeTax: Number(incomeTax.toFixed(2)),
      dsmfEmployee: Number(dsmfEmployee.toFixed(2)),
      itsEmployee: Number(itsEmployee.toFixed(2)),
      unemploymentEmployee: Number(unemploymentEmployee.toFixed(2)),
      unionFee: Number(unionFee.toFixed(2)),
      totalEmployeeDeductions: Number(totalEmployeeDeductions.toFixed(2)),
      dsmfEmployer: Number(dsmfEmployer.toFixed(2)),
      itsEmployer: Number(itsEmployer.toFixed(2)),
      unemploymentEmployer: Number(unemploymentEmployer.toFixed(2)),
      totalEmployerCost: Number(totalEmployerCost.toFixed(2)),
      superGross: Number(superGross.toFixed(2))
    };
  };

  if (mode === 'GROSS_TO_NET') {
    return calculate(Number(amount) || 0);
  } else {
    const targetNet = Number(amount) || 0;
    if (targetNet <= 0) return calculate(0);

    let low = targetNet;
    let high = targetNet * 3;
    let mid = 0;
    for (let i = 0; i < 30; i++) {
      mid = (low + high) / 2;
      const res = calculate(mid);
      if (res.net < targetNet) {
        low = mid;
      } else {
        high = mid;
      }
    }
    return calculate(mid);
  }
};
